/**
 * MethodologyConnectionChip
 *
 * A pill badge that represents a single methodology connection between
 * a clarity artifact (outcome driver, back plan node, OST node, open
 * question) and a prioritization domain object. Clicking the chip
 * navigates to the source artifact.
 *
 * Used in ItemDetailPage to surface "what clarity context underpins this item?"
 */

import { Link } from 'react-router-dom';
import {
  Map,
  Target,
  HelpCircle,
  TrendingUp,
  ExternalLink,
  Crosshair,
  Flag,
  BarChart2,
  Navigation,
} from 'lucide-react';
import type { MethodologyConnection, MethodologySourceType, MethodologyConnectionType } from '../../lib/methodologyConnectionsService';

// ── Labels and styling ────────────────────────────────────────

const SOURCE_TYPE_LABELS: Record<MethodologySourceType, string> = {
  outcome_driver: 'Outcome Driver',
  desired_outcome: 'Desired Outcome',
  backplan_node: 'Back Plan',
  roadmap_node: 'Roadmap',
  ost_node: 'OST',
  open_question: 'Open Question',
  objective: 'Objective',
  goal: 'Goal',
};

const SOURCE_TYPE_ICONS: Record<MethodologySourceType, React.ReactNode> = {
  outcome_driver: <TrendingUp className="h-3 w-3" />,
  desired_outcome: <Crosshair className="h-3 w-3" />,
  backplan_node: <Map className="h-3 w-3" />,
  roadmap_node: <Navigation className="h-3 w-3" />,
  ost_node: <Target className="h-3 w-3" />,
  open_question: <HelpCircle className="h-3 w-3" />,
  objective: <Flag className="h-3 w-3" />,
  goal: <BarChart2 className="h-3 w-3" />,
};

const SOURCE_TYPE_COLORS: Record<MethodologySourceType, string> = {
  outcome_driver: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)] dark:border-[var(--mustard)]',
  desired_outcome: 'bg-[var(--green-dim)] text-[var(--green)] border-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)] dark:border-[var(--green)]',
  backplan_node: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] dark:bg-[var(--slate)]/20 dark:border-[var(--slate)]',
  roadmap_node:
    'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200 dark:bg-fuchsia-950/40 dark:text-fuchsia-300 dark:border-fuchsia-800',
  ost_node: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] dark:bg-[var(--slate)]/20 dark:border-[var(--slate)]',
  open_question: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] dark:bg-[var(--slate)]/20 dark:border-[var(--slate)]',
  objective: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)] dark:bg-[var(--slate)]/20 dark:border-[var(--slate)]',
  goal: 'bg-[var(--green-dim)] text-[var(--green)] border-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)] dark:border-[var(--green)]',
};

const CONNECTION_TYPE_LABELS: Record<MethodologyConnectionType, string> = {
  informs: 'informs',
  motivates: 'motivates',
  addresses: 'addresses',
  answers: 'answers',
  derives_from: 'derives from',
};

// ── Source URL resolver ───────────────────────────────────────

function resolveSourceUrl(connection: MethodologyConnection): string | null {
  switch (connection.sourceEntityType) {
    case 'backplan_node':
      return `/prioritization/backplan`;
    case 'roadmap_node':
      return `/planning/roadmap`;
    case 'ost_node':
      return `/prioritization/strategy`;
    case 'open_question':
      return `/prioritization/open-questions`;
    case 'outcome_driver':
      return `/prioritization/outcome-drivers`;
    case 'desired_outcome':
      return `/prioritization/desired-outcomes/${connection.sourceEntityId}`;
    case 'objective':
      return `/prioritization/strategy/objectives`;
    case 'goal':
      return `/prioritization/strategy/objectives?tab=goals`;
    default:
      return null;
  }
}

// ── Component ─────────────────────────────────────────────────

interface MethodologyConnectionChipProps {
  connection: MethodologyConnection;
  label?: string;
  onRemove?: () => void;
}

export function MethodologyConnectionChip({
  connection,
  label,
  onRemove,
}: MethodologyConnectionChipProps) {
  const url = resolveSourceUrl(connection);
  const colorClass = SOURCE_TYPE_COLORS[connection.sourceEntityType];
  const icon = SOURCE_TYPE_ICONS[connection.sourceEntityType];
  const typeLabel = SOURCE_TYPE_LABELS[connection.sourceEntityType];
  const connLabel = CONNECTION_TYPE_LABELS[connection.connectionType];

  const content = (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border
        ${colorClass} transition-opacity`}
    >
      {icon}
      <span className="opacity-60">{connLabel}</span>
      <span>{label ?? typeLabel}</span>
      {url && <ExternalLink className="h-2.5 w-2.5 opacity-50" />}
      {onRemove && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 opacity-50 hover:opacity-100 transition-opacity"
          title="Remove connection"
        >
          ×
        </button>
      )}
    </span>
  );

  if (url) {
    return <Link to={url}>{content}</Link>;
  }
  return <span>{content}</span>;
}

// ── Clarity Section (aggregate display) ──────────────────────

interface ClaritySectionProps {
  connections: MethodologyConnection[];
  onRemoveConnection?: (id: string) => void;
}

export function ClaritySection({ connections, onRemoveConnection }: ClaritySectionProps) {
  if (connections.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {connections.map((conn) => (
        <MethodologyConnectionChip
          key={conn.id}
          connection={conn}
          onRemove={onRemoveConnection ? () => onRemoveConnection(conn.id) : undefined}
        />
      ))}
    </div>
  );
}
